import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Skeleton, notification } from 'antd'
import { useState, type JSX } from 'react'

import { SettingsPage } from '../Settings'

import { AddDomainForm } from './AddDomainForm'
import { isDomainNotFoundForReuse, isReusableDomainConflict } from './customDomainErrors'
import { DomainStatusView } from './DomainStatusView'
import { RemoveDomainModal } from './RemoveDomainModal'
import { ReuseDomainModal } from './ReuseDomainModal'
import { hasConfiguredCustomDomain } from './utils'

import { createCustomDomain, deleteCustomDomain, verifyCustomDomain } from 'api/customDomain'
import { getSettings } from 'api/signUp'
import { CAPABILITIES } from 'constants/capabilities'
import { TOGGLE_CUSTOM_DOMAIN_TAB } from 'constants/launch-darkly-flags'
import { QUERIES } from 'constants/queries'
import { useHasFlag } from 'contexts/LaunchDarklyProvider'
import { useHasCapability } from 'hooks/useCapabilities'

const TERMINAL_CLOUDFRONT_STATUSES = ['active', 'failed']
const STATUS_POLL_INTERVAL_MS = 5000

export type CustomDomainProps = {
  client: { id: string }
  whiteLabelEnabled: boolean
}

export function CustomDomain({ client, whiteLabelEnabled }: CustomDomainProps): JSX.Element | null {
  const queryClient = useQueryClient()
  const hasCustomDomainFlag = useHasFlag(TOGGLE_CUSTOM_DOMAIN_TAB)
  const hasCustomDomainCapability = useHasCapability(CAPABILITIES.ADMIN.CUSTOM_DOMAIN.WRITE)
  const [domainInput, setDomainInput] = useState('')
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)
  const [removeConfirmInput, setRemoveConfirmInput] = useState('')
  const [reusePromptDomain, setReusePromptDomain] = useState<string | null>(null)

  const { data: settings, isLoading } = useQuery(
    [QUERIES.CLIENT_SETTINGS, { id: client.id }],
    () => getSettings(client.id),
    {
      refetchInterval: (data) => {
        const status = data?.customDomain?.cloudfrontStatus
        return status && !TERMINAL_CLOUDFRONT_STATUSES.includes(status) ? STATUS_POLL_INTERVAL_MS : false
      },
      onError: () => {
        notification.error({ message: 'Could not fetch client settings', placement: 'bottomRight', duration: 2 })
      },
    },
  )

  function invalidateSettings() {
    queryClient.invalidateQueries([QUERIES.CLIENT_SETTINGS, { id: client.id }])
  }

  function openRemoveModal() {
    setRemoveConfirmInput('')
    setIsRemoveModalOpen(true)
  }

  function closeRemoveModal() {
    setRemoveConfirmInput('')
    setIsRemoveModalOpen(false)
  }

  const { mutate: addDomain, isLoading: isAdding } = useMutation(
    () => createCustomDomain({ client_id: client.id, domain: domainInput.trim() }),
    {
      onSuccess: () => {
        notification.success({ message: 'Custom domain added', placement: 'bottomRight', duration: 2 })
        setDomainInput('')
        invalidateSettings()
      },
      onError: (error: any) => {
        if (isReusableDomainConflict(error)) {
          setReusePromptDomain(domainInput.trim())
          return
        }

        const errorMessage = error?.errors?.[0]?.message || error?.errors?.[0]?.detail || 'Could not add custom domain'
        notification.error({ message: errorMessage, placement: 'bottomRight', duration: 4 })
      },
    },
  )

  const { mutate: reuseDomain, isLoading: isReusing } = useMutation(
    () => createCustomDomain({ client_id: client.id, domain: reusePromptDomain ?? '', reuse_existing_domain: true }),
    {
      onSuccess: () => {
        notification.success({
          message: 'Custom domain associated with the shared domain',
          placement: 'bottomRight',
          duration: 2,
        })
        setDomainInput('')
        setReusePromptDomain(null)
        invalidateSettings()
      },
      onError: (error: any) => {
        setReusePromptDomain(null)

        const errorMessage = isDomainNotFoundForReuse(error)
          ? 'This domain is no longer available to reuse. Resubmit to add it as a new domain instead.'
          : 'Could not associate this leader with the shared domain'
        notification.error({ message: errorMessage, placement: 'bottomRight', duration: 4 })
      },
    },
  )

  const { mutate: removeDomain, isLoading: isRemoving } = useMutation(
    () => deleteCustomDomain({ client_id: client.id }),
    {
      onSuccess: () => {
        notification.success({ message: 'Custom domain removed', placement: 'bottomRight', duration: 2 })
        invalidateSettings()
      },
      onError: () => {
        notification.error({ message: 'Could not remove custom domain', placement: 'bottomRight', duration: 2 })
      },
    },
  )

  const { mutate: verifyDomain, isLoading: isVerifying } = useMutation(
    () => verifyCustomDomain({ client_id: client.id }),
    {
      onSuccess: () => {
        invalidateSettings()
        notification.success({ message: 'Status refreshed', placement: 'bottomRight', duration: 2 })
      },
      onError: () => {
        notification.error({ message: 'Could not verify custom domain', placement: 'bottomRight', duration: 2 })
      },
    },
  )

  if (!hasCustomDomainFlag) {
    return null
  }

  if (isLoading) {
    return (
      <SettingsPage title="Custom Domain">
        <Skeleton active paragraph={{ rows: 6 }} />
      </SettingsPage>
    )
  }

  const customDomain = settings?.customDomain

  return (
    <SettingsPage description="Serve member-facing pages from the client’s own domain." title="Custom Domain">
      {!hasConfiguredCustomDomain(customDomain) ? (
        <>
          <AddDomainForm
            canWrite={hasCustomDomainCapability}
            domainInput={domainInput}
            isAdding={isAdding}
            onDomainInputChange={setDomainInput}
            onSubmit={() => addDomain()}
            whiteLabelEnabled={whiteLabelEnabled}
          />
          <ReuseDomainModal
            domain={reusePromptDomain ?? ''}
            isOpen={reusePromptDomain !== null}
            isSubmitting={isReusing}
            onCancel={() => setReusePromptDomain(null)}
            onConfirm={() => reuseDomain()}
          />
        </>
      ) : (
        <>
          <DomainStatusView
            canWrite={hasCustomDomainCapability}
            cloudfrontStatus={customDomain.cloudfrontStatus}
            dnsRecords={customDomain.dnsRecords}
            domain={customDomain.domain}
            isRemoving={isRemoving}
            isVerifying={isVerifying}
            onOpenRemove={openRemoveModal}
            onVerify={() => verifyDomain()}
          />
          <RemoveDomainModal
            confirmInput={removeConfirmInput}
            domain={customDomain.domain}
            isOpen={isRemoveModalOpen}
            isRemoving={isRemoving}
            onCancel={closeRemoveModal}
            onConfirm={() => {
              closeRemoveModal()
              removeDomain()
            }}
            onConfirmInputChange={setRemoveConfirmInput}
          />
        </>
      )}
    </SettingsPage>
  )
}
